import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  ClipboardList, CheckCircle2, XCircle, AlertCircle, Clock,
  User, Home, CreditCard, Dog, Calendar, MessageSquare,
  Settings, Shield, Search, Share2, Heart, Camera, FileText,
  ArrowLeft, Send, Users, Crown, Megaphone, BookOpen, Globe
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Test sections with test cases
const TEST_SECTIONS = [
  {
    id: 'auth',
    name: 'Authentication & Account',
    icon: User,
    description: 'Login, registration, password reset, and account settings',
    estimatedTime: '10-15 min',
    testCases: [
      { id: 'auth-1', name: 'Register new account', steps: 'Go to /register, fill form, verify email confirmation message appears', expected: 'Account created, redirected to dashboard' },
      { id: 'auth-2', name: 'Login with email/password', steps: 'Go to /login, enter credentials, click Sign In', expected: 'Logged in, redirected to dashboard' },
      { id: 'auth-3', name: 'Login with Google', steps: 'Click "Continue with Google" on login page', expected: 'Google OAuth flow completes, logged in' },
      { id: 'auth-4', name: 'Logout', steps: 'Click profile menu > Logout', expected: 'Logged out, redirected to landing page' },
      { id: 'auth-5', name: 'Update profile settings', steps: 'Go to /settings, change display name, save', expected: 'Settings saved, name updated everywhere' },
      { id: 'auth-6', name: 'Change password', steps: 'Go to /settings > Security, enter old/new password, save', expected: 'Password changed, can login with new password' },
    ]
  },
  {
    id: 'membership',
    name: 'Membership & Payments',
    icon: CreditCard,
    description: 'Subscription tiers, upgrades, and Stripe checkout',
    estimatedTime: '15-20 min',
    testCases: [
      { id: 'mem-1', name: 'View membership options', steps: 'Go to /membership/upgrade, review tier comparison', expected: 'All 3 tiers shown with features listed' },
      { id: 'mem-2', name: 'Start checkout (test mode)', steps: 'Click upgrade button, verify Stripe checkout loads', expected: 'Stripe checkout page appears with correct price' },
      { id: 'mem-3', name: 'View current membership', steps: 'Go to /settings > Membership tab', expected: 'Current tier and benefits displayed' },
      { id: 'mem-4', name: 'Identity verification flow', steps: 'Go to /settings > Identity, start verification', expected: 'Stripe Identity or card verification starts' },
    ]
  },
  {
    id: 'pets',
    name: 'Pet Profiles',
    icon: Dog,
    description: 'Adding, editing, and managing pet profiles',
    estimatedTime: '15-20 min',
    testCases: [
      { id: 'pet-1', name: 'Add a new pet', steps: 'Go to /my-pets, click Add Pet, fill form with photo', expected: 'Pet created, appears in pet list' },
      { id: 'pet-2', name: 'Edit pet details', steps: 'Click on pet card, edit name/breed/weight, save', expected: 'Changes saved and displayed' },
      { id: 'pet-3', name: 'Upload pet photo', steps: 'In pet edit mode, upload new photo', expected: 'Photo uploads and displays correctly' },
      { id: 'pet-4', name: 'View pet profile page', steps: 'Click "View Profile" on pet card', expected: 'Full pet profile page loads with all details' },
      { id: 'pet-5', name: 'Delete a pet', steps: 'In pet edit mode, click Delete, confirm', expected: 'Pet removed from list' },
      { id: 'pet-6', name: 'Add health record', steps: 'Go to pet profile > Health tab, add vaccination', expected: 'Health record saved and displayed' },
    ]
  },
  {
    id: 'social',
    name: 'Social Features',
    icon: Users,
    description: 'Following, sharing, and community interactions',
    estimatedTime: '15-20 min',
    testCases: [
      { id: 'soc-1', name: 'Follow another user', steps: 'Find a user profile, click Follow button', expected: 'Follow status updates, appears in your following list' },
      { id: 'soc-2', name: 'Unfollow a user', steps: 'Go to followed user, click Unfollow', expected: 'Unfollowed, removed from following list' },
      { id: 'soc-3', name: 'Share a pet profile', steps: 'On pet profile, click Share, copy link', expected: 'Link copied, shareable URL works' },
      { id: 'soc-4', name: 'View activity feed', steps: 'Go to /feed or dashboard', expected: 'Recent activity from followed users appears' },
      { id: 'soc-5', name: 'Send a message (if available)', steps: 'Go to user profile, click Message', expected: 'Message compose opens or feature indicator shown' },
    ]
  },
  {
    id: 'events',
    name: 'Events & Calendar',
    icon: Calendar,
    description: 'Browsing, creating, and RSVPing to events',
    estimatedTime: '15-20 min',
    testCases: [
      { id: 'evt-1', name: 'Browse events list', steps: 'Go to /events, scroll through list', expected: 'Events displayed with dates, locations, RSVP counts' },
      { id: 'evt-2', name: 'Filter events by type', steps: 'Use filter dropdown or tabs', expected: 'Events filtered correctly' },
      { id: 'evt-3', name: 'View event details', steps: 'Click on an event card', expected: 'Full event page with description, location, attendees' },
      { id: 'evt-4', name: 'RSVP to event', steps: 'Click RSVP/Attend button on event', expected: 'RSVP confirmed, added to your events' },
      { id: 'evt-5', name: 'Cancel RSVP', steps: 'Go to event you RSVPd to, click Cancel', expected: 'RSVP removed' },
      { id: 'evt-6', name: 'Create new event (if admin)', steps: 'Click Create Event, fill form, submit', expected: 'Event created and visible in list' },
    ]
  },
  {
    id: 'content',
    name: 'Blog & Content',
    icon: BookOpen,
    description: 'Reading articles, news feeds, and curated content',
    estimatedTime: '10-15 min',
    testCases: [
      { id: 'con-1', name: 'Browse blog articles', steps: 'Go to /club-blog, scroll through posts', expected: 'Articles displayed with titles, excerpts, dates' },
      { id: 'con-2', name: 'Read full article', steps: 'Click on article card', expected: 'Full article page loads with content' },
      { id: 'con-3', name: 'View RSS feed directory', steps: 'Go to /feeds', expected: 'List of news sources with article counts' },
      { id: 'con-4', name: 'Filter by content source', steps: 'On blog, filter by source (GNN, WDJ, etc.)', expected: 'Only selected source articles shown' },
      { id: 'con-5', name: 'External article attribution', steps: 'Open curated article, check source link', expected: 'Original source link present and works' },
    ]
  },
  {
    id: 'search',
    name: 'Search & Discovery',
    icon: Search,
    description: 'Finding pets, users, events, and content',
    estimatedTime: '10-15 min',
    testCases: [
      { id: 'sea-1', name: 'Global search', steps: 'Use search bar in header, type query', expected: 'Results from multiple categories appear' },
      { id: 'sea-2', name: 'Search for breeds', steps: 'Go to /breeds, search for "Golden"', expected: 'Golden Retriever and related breeds shown' },
      { id: 'sea-3', name: 'Search for users', steps: 'Search for a username', expected: 'User profiles matching query appear' },
      { id: 'sea-4', name: 'Search for services', steps: 'Go to /services, search by location or type', expected: 'Relevant services displayed' },
      { id: 'sea-5', name: 'Browse dog parks', steps: 'Go to /parks, browse or search', expected: 'Dog parks with ratings, amenities shown' },
    ]
  },
  {
    id: 'clubs',
    name: 'Clubs & Organizations',
    icon: Globe,
    description: 'Joining clubs, viewing club content, club switching',
    estimatedTime: '15-20 min',
    testCases: [
      { id: 'club-1', name: 'Browse available clubs', steps: 'Go to /organizations', expected: 'List of clubs with descriptions' },
      { id: 'club-2', name: 'View club details', steps: 'Click on a club card', expected: 'Club page with info, members, events' },
      { id: 'club-3', name: 'Join a club', steps: 'Click Join button on club page', expected: 'Membership confirmed, club added to your list' },
      { id: 'club-4', name: 'Switch between clubs', steps: 'Use club switcher in header', expected: 'Context switches, branding updates' },
      { id: 'club-5', name: 'View club-specific events', steps: 'On club page, go to Events tab', expected: 'Only that clubs events shown' },
    ]
  },
  {
    id: 'creator',
    name: 'Club Creator (Premium)',
    icon: Crown,
    description: 'Creating and managing clubs (requires Premium)',
    estimatedTime: '20-25 min',
    premium: true,
    testCases: [
      { id: 'cre-1', name: 'Access Creator Dashboard', steps: 'Click profile > Creator Dashboard', expected: 'Dashboard loads (or upgrade prompt for non-premium)' },
      { id: 'cre-2', name: 'Create a new club', steps: 'Click Create Club, fill form, submit', expected: 'Club created, appears in My Clubs' },
      { id: 'cre-3', name: 'Toggle club features', steps: 'In Creator Dashboard > Features tab, toggle options', expected: 'Features enable/disable, changes saved' },
      { id: 'cre-4', name: 'Manage ad policy', steps: 'Go to Ad Policy tab, toggle settings', expected: 'Ad policy updated' },
      { id: 'cre-5', name: 'Promote member to admin', steps: 'Go to Members tab, find member, click Make Admin', expected: 'User promoted, badge updates' },
      { id: 'cre-6', name: 'Demote admin to member', steps: 'Find admin, click Remove Admin', expected: 'User demoted, badge updates' },
    ]
  },
  {
    id: 'mobile',
    name: 'Mobile Responsiveness',
    icon: Settings,
    description: 'Testing on mobile devices or narrow browser windows',
    estimatedTime: '15-20 min',
    testCases: [
      { id: 'mob-1', name: 'Landing page mobile', steps: 'View landing page on phone or resize browser to 375px', expected: 'Content readable, no horizontal scroll' },
      { id: 'mob-2', name: 'Navigation menu mobile', steps: 'Open hamburger menu on mobile', expected: 'Menu opens, all links accessible' },
      { id: 'mob-3', name: 'Dashboard mobile', steps: 'View dashboard on mobile', expected: 'Cards stack vertically, readable' },
      { id: 'mob-4', name: 'Forms mobile', steps: 'Fill out login/register form on mobile', expected: 'Inputs accessible, keyboard doesnt block' },
      { id: 'mob-5', name: 'Pet profile mobile', steps: 'View pet profile on mobile', expected: 'Photos and info display correctly' },
    ]
  },
  {
    id: 'legal',
    name: 'Legal & Compliance Pages',
    icon: FileText,
    description: 'Privacy policy, disclaimer, and terms pages',
    estimatedTime: '5-10 min',
    testCases: [
      { id: 'leg-1', name: 'Privacy Policy page', steps: 'Go to /privacy-policy', expected: 'Full privacy policy displays' },
      { id: 'leg-2', name: 'Disclaimer page', steps: 'Go to /disclaimer', expected: 'All 8 disclaimer sections display' },
      { id: 'leg-3', name: 'Cookie consent banner', steps: 'Clear cookies, reload site', expected: 'Privacy choices banner appears' },
      { id: 'leg-4', name: 'Cookie preferences', steps: 'Click Cookie Preferences in footer', expected: 'Modal opens with 3 options' },
      { id: 'leg-5', name: 'Footer links work', steps: 'Click each link in footer', expected: 'All links navigate correctly' },
    ]
  },
  {
    id: 'admin-setup',
    name: 'Admin Setup & Seeding',
    icon: Settings,
    description: 'Database seeding and admin operations (requires bootstrap key)',
    estimatedTime: '10-15 min',
    admin: true,
    testCases: [
      { id: 'adm-1', name: 'Access Admin Setup page', steps: 'Go to /admin/setup', expected: 'Admin Setup page loads with Bootstrap Key input' },
      { id: 'adm-2', name: 'Verify bootstrap key', steps: 'Enter bootstrap key, click Verify', expected: 'Key verified, action buttons become enabled' },
      { id: 'adm-3', name: 'Seed ALL Structure Data', steps: 'Click "Seed ALL Structure Data" button', expected: 'Clubs, sources, breeds, FAQs, etc. created with success message' },
      { id: 'adm-4', name: 'Seed ALL Content Data', steps: 'Click "Seed ALL Content Data" button', expected: 'Dog parks, blog posts, agencies seeded with counts shown' },
      { id: 'adm-5', name: 'Create Database Indexes', steps: 'Click "Create Database Indexes" button', expected: 'Indexes created/skipped with details shown' },
      { id: 'adm-6', name: 'Seed Club Announcements', steps: 'Click "Seed Club Announcements" button', expected: 'Welcome announcements created for clubs without one' },
      { id: 'adm-7', name: 'Import RSS Feeds', steps: 'Click "Import All RSS Feeds" button', expected: 'Articles imported from active RSS sources' },
      { id: 'adm-8', name: 'Create Admin User', steps: 'Fill email/password, click Create/Update Admin', expected: 'Admin user created or updated successfully' },
    ]
  },
  {
    id: 'guest-identity',
    name: 'Guest Identity & Anti-Abuse',
    icon: Shield,
    description: 'Anonymous user tracking and rate limiting',
    estimatedTime: '10-15 min',
    testCases: [
      { id: 'gst-1', name: 'Guest session created on visit', steps: 'Open site in incognito, check localStorage for aadc_guest_id', expected: 'Guest ID stored in localStorage' },
      { id: 'gst-2', name: 'Page views tracked', steps: 'Browse several pages, check Network tab for /api/guest/track calls', expected: 'Page view tracking requests sent' },
      { id: 'gst-3', name: 'CAPTCHA marks session verified', steps: 'Go to /register, complete CAPTCHA (dont submit)', expected: 'Guest session marked as captcha_verified' },
      { id: 'gst-4', name: 'Guest merged on registration', steps: 'Complete registration after browsing as guest', expected: 'Guest browsing interests transferred to user profile' },
      { id: 'gst-5', name: 'Guest merged on login', steps: 'Browse as guest, then login to existing account', expected: 'Guest session merged to user' },
      { id: 'gst-6', name: 'Admin can view guest stats', steps: 'GET /api/guest/admin/stats with admin token', expected: 'Returns total sessions, verified, blocked, conversion rate' },
    ]
  },
  {
    id: 'club-announcements',
    name: 'Club Announcements',
    icon: Megaphone,
    description: 'Club-specific announcements and welcome messages',
    estimatedTime: '10-15 min',
    testCases: [
      { id: 'ann-1', name: 'View club announcements', steps: 'Go to /club/all-american-dog-club, scroll to Announcements section', expected: 'Announcements section visible with welcome message' },
      { id: 'ann-2', name: 'Pinned announcement styling', steps: 'Check welcome announcement appearance', expected: 'Pinned icon shown, amber/highlighted background' },
      { id: 'ann-3', name: 'Auto-generated welcome on club create', steps: 'Create a new club with "why" selections', expected: 'Welcome announcement auto-created with why reasons' },
      { id: 'ann-4', name: 'Club-specific filtering', steps: 'View different clubs, check announcements', expected: 'Each club shows only its own announcements' },
      { id: 'ann-5', name: 'Announcement API returns club_id', steps: 'GET /api/announcements/?club_id={id}', expected: 'Only announcements for that club returned' },
    ]
  },
  {
    id: 'club-wizard',
    name: 'Club Creation Wizard',
    icon: Crown,
    description: '7-step wizard for creating new clubs (requires Premium)',
    estimatedTime: '20-25 min',
    premium: true,
    testCases: [
      { id: 'wiz-1', name: 'Access wizard (Premium required)', steps: 'Go to /clubs/create', expected: 'Wizard loads for Premium users, upgrade prompt for others' },
      { id: 'wiz-2', name: 'Step 1: Basic Info', steps: 'Fill name, slug, tagline, motto, description', expected: 'Slug auto-generates, all fields save' },
      { id: 'wiz-3', name: 'Step 2: Pet Types', steps: 'Select pet types (can select multiple + Other)', expected: 'Pet types saved, Other shows text input' },
      { id: 'wiz-4', name: 'Step 3: Location + Language', steps: 'Select country, state, region, preferred language', expected: 'Location saved, similar clubs check triggered' },
      { id: 'wiz-5', name: 'Similar clubs check', steps: 'After location step, if similar clubs exist', expected: 'Modal shows similar clubs with option to continue or view' },
      { id: 'wiz-6', name: 'Step 4: Branding', steps: 'Select color preset or custom colors', expected: 'Live preview updates, colors saved' },
      { id: 'wiz-7', name: 'Step 5: Features', steps: 'Toggle club features on/off', expected: 'Feature selections saved' },
      { id: 'wiz-8', name: 'Step 6: Why Creating', steps: 'Select why reasons (checkboxes + Other)', expected: 'Selections saved, tip about welcome message shown' },
      { id: 'wiz-9', name: 'Step 7: Agreements', steps: 'Read and accept Service Agreement and Bylaws', expected: 'Both agreements must be accepted to proceed' },
      { id: 'wiz-10', name: 'Submit and create club', steps: 'Click Create Club on final step', expected: 'Club created, welcome announcement generated, redirected to club page' },
      { id: 'wiz-11', name: 'Auto-follow creator on join', steps: 'Have another user join the new club', expected: 'New member automatically follows club creator' },
    ]
  }
];

export default function BetaTestPlan() {
  const { user, token } = useAuth();
  const [claimedSections, setClaimedSections] = useState({});
  const [testResults, setTestResults] = useState({});
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loadingClaims, setLoadingClaims] = useState(true);

  // Load existing claims and results
  const loadTestData = useCallback(async () => {
    if (!token) {
      setLoadingClaims(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/api/beta/test-claims`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClaimedSections(response.data.claims || {});
      setTestResults(response.data.results || {});
    } catch (error) {
      // Endpoint may not exist yet - that's ok
      
    } finally {
      setLoadingClaims(false);
    }
  }, [token]);

  useEffect(() => {
    loadTestData();
  }, [loadTestData]);

  // Claim a section
  const claimSection = async (sectionId) => {
    if (!user) {
      toast.error('Please login to claim a test section');
      return;
    }
    
    setClaimedSections(prev => ({
      ...prev,
      [sectionId]: { claimedBy: user.username || user.email, claimedAt: new Date().toISOString() }
    }));
    
    // Try to save to backend
    try {
      await axios.post(`${API}/api/beta/test-claims`, {
        section_id: sectionId,
        action: 'claim'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      // Continue locally even if backend save fails
    }
    
    toast.success(`You claimed the "${TEST_SECTIONS.find(s => s.id === sectionId)?.name}" section!`);
  };

  // Toggle test result
  const toggleTestResult = (testId, result) => {
    setTestResults(prev => ({
      ...prev,
      [testId]: { result, testedBy: user?.username || user?.email, testedAt: new Date().toISOString() }
    }));
  };

  // Submit feedback
  const submitFeedback = async () => {
    if (!feedback.trim()) {
      toast.error('Please enter your feedback');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/beta/feedback`, {
        type: 'qa_test_results',
        results: testResults,
        feedback: feedback,
        sections_tested: Object.keys(claimedSections)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Thank you! Your test results have been submitted.');
      setFeedback('');
    } catch (error) {
      // Save locally
      
      toast.success('Results saved locally. Backend submission will retry later.');
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate progress
  const totalTests = TEST_SECTIONS.reduce((acc, s) => acc + s.testCases.length, 0);
  const completedTests = Object.keys(testResults).length;
  const passedTests = Object.values(testResults).filter(r => r.result === 'pass').length;
  const failedTests = Object.values(testResults).filter(r => r.result === 'fail').length;

  return (
    <div className="min-h-screen bg-muted/30 py-8" data-testid="beta-test-plan">
      <div className="container mx-auto px-4 max-w-5xl">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/beta-feedback" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Beta Hub
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <ClipboardList className="w-8 h-8 text-primary" />
            <h1 className="text-2xl font-heading font-medium">Beta Test Plan</h1>
          </div>
          <p className="text-muted-foreground">
            Help us test the platform! Claim a section below and work through the test cases. 
            Report your results to help us find and fix issues.
          </p>
        </div>

        {/* Progress Overview */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium">Overall Testing Progress</h3>
              <span className="text-sm text-muted-foreground">
                {completedTests} / {totalTests} tests completed
              </span>
            </div>
            <Progress value={(completedTests / totalTests) * 100} className="h-2 mb-4" />
            <div className="flex gap-6 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <span>{passedTests} Passed</span>
              </div>
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-500" />
                <span>{failedTests} Failed</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span>{totalTests - completedTests} Remaining</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Test Sections */}
        <div className="space-y-6">
          {TEST_SECTIONS.map(section => {
            const Icon = section.icon;
            const isClaimed = claimedSections[section.id];
            const sectionTests = section.testCases.map(tc => testResults[tc.id]).filter(Boolean);
            const sectionComplete = sectionTests.length === section.testCases.length;
            const sectionPassed = sectionTests.filter(r => r.result === 'pass').length;
            const sectionFailed = sectionTests.filter(r => r.result === 'fail').length;
            
            return (
              <Card key={section.id} className={section.premium ? 'border-amber-200 bg-amber-50/30' : ''}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        section.premium ? 'bg-amber-100 text-amber-600' : 'bg-primary/10 text-primary'
                      }`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          {section.name}
                          {section.premium && (
                            <Badge className="bg-amber-500 text-xs">Premium</Badge>
                          )}
                          {sectionComplete && (
                            <Badge variant="outline" className="text-green-600 border-green-600 text-xs">
                              Complete
                            </Badge>
                          )}
                        </CardTitle>
                        <CardDescription>{section.description}</CardDescription>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-muted-foreground mb-2">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {section.estimatedTime}
                      </div>
                      {isClaimed ? (
                        <Badge variant="secondary">
                          Claimed by {isClaimed.claimedBy === (user?.username || user?.email) ? 'you' : isClaimed.claimedBy}
                        </Badge>
                      ) : (
                        <Button size="sm" onClick={() => claimSection(section.id)}>
                          Claim Section
                        </Button>
                      )}
                    </div>
                  </div>
                  {sectionTests.length > 0 && (
                    <div className="flex gap-4 mt-3 text-xs">
                      <span className="text-green-600">{sectionPassed} passed</span>
                      <span className="text-red-600">{sectionFailed} failed</span>
                      <span className="text-muted-foreground">
                        {section.testCases.length - sectionTests.length} remaining
                      </span>
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {section.testCases.map(tc => {
                      const result = testResults[tc.id];
                      return (
                        <div 
                          key={tc.id} 
                          className={`p-3 rounded-lg border ${
                            result?.result === 'pass' ? 'bg-green-50 border-green-200' :
                            result?.result === 'fail' ? 'bg-red-50 border-red-200' :
                            'bg-background'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="font-medium text-sm">{tc.name}</div>
                              <div className="text-xs text-muted-foreground mt-1">
                                <strong>Steps:</strong> {tc.steps}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                <strong>Expected:</strong> {tc.expected}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant={result?.result === 'pass' ? 'default' : 'outline'}
                                className={result?.result === 'pass' ? 'bg-green-600 hover:bg-green-700' : ''}
                                onClick={() => toggleTestResult(tc.id, 'pass')}
                              >
                                <CheckCircle2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant={result?.result === 'fail' ? 'default' : 'outline'}
                                className={result?.result === 'fail' ? 'bg-red-600 hover:bg-red-700' : ''}
                                onClick={() => toggleTestResult(tc.id, 'fail')}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Feedback Submission */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Submit Your Results & Feedback
            </CardTitle>
            <CardDescription>
              Share any bugs found, suggestions, or general feedback about your testing experience
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              placeholder="Describe any issues found, steps to reproduce bugs, or suggestions for improvement..."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={5}
              data-testid="feedback-textarea"
            />
            <div className="flex justify-between items-center">
              <div className="text-sm text-muted-foreground">
                {completedTests > 0 && (
                  <span>You've tested {completedTests} items ({passedTests} passed, {failedTests} failed)</span>
                )}
              </div>
              <Button onClick={submitFeedback} disabled={submitting}>
                {submitting ? (
                  <>Submitting...</>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Results
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card className="mt-8 bg-blue-50/50 border-blue-200">
          <CardContent className="p-6">
            <h3 className="font-medium mb-3 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-blue-500" />
              How to Use This Test Plan
            </h3>
            <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
              <li><strong>Claim a section</strong> - Pick an area you'd like to test and click "Claim Section"</li>
              <li><strong>Work through test cases</strong> - Follow the steps for each test case</li>
              <li><strong>Mark results</strong> - Click the green check if it works, red X if something's wrong</li>
              <li><strong>Note any issues</strong> - Use the feedback box to describe bugs or unexpected behavior</li>
              <li><strong>Submit your results</strong> - Send in your findings so we can improve the app</li>
            </ol>
            <p className="text-sm text-muted-foreground mt-4">
              <strong>Tip:</strong> If you find a bug, note the exact steps to reproduce it and what you expected vs. what happened.
              Screenshots are super helpful!
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
